import type { FreelanceSheetData } from "@/domains/company/active-search/types";
import type { ClientMembershipEntry, PoolMatchFreelancer } from "../api/types";

// The three visual states a pool match card can take.
export type PoolCardPresentation = "normal" | "unavailable" | "removed";

/**
 * Pure resolver for a pool match card's presentation.
 *
 * - `unavailable` when the freelancer is no longer discoverable (`!isDiscoverable`)
 *   — attenuated, profile still viewable but contact disabled (D9/OQ4). Takes
 *   precedence: a non-discoverable row is unavailable even if also un-hearted.
 * - `removed` when the membership entry has disappeared AFTER seeding
 *   (`seeded && entry === undefined`) — the un-heart signal (AC12): attenuated +
 *   "will disappear on next refresh" microcopy. Never `removed` before the seed
 *   resolves (an entry that is merely not-yet-seeded reads as `normal`).
 * - `normal` otherwise.
 *
 * Pure and DOM-free (node-testable).
 */
export const derivePoolCardPresentation = (
  entry: ClientMembershipEntry | undefined,
  isDiscoverable: boolean,
  seeded: boolean,
): PoolCardPresentation => {
  if (!isDiscoverable) return "unavailable";
  if (seeded && entry === undefined) return "removed";
  return "normal";
};

/**
 * Sheet/card adapter (D7) — the boundary between the Talent Pool match tab and
 * the shared `FreelanceCard`/`FreelanceSheet` (and their `HeartButton`/
 * `SheetSaveButton`, which read `search_id` unconditionally). Deliberately an
 * ALLOW-LIST, not a pass-through: `PoolMatchFreelancer` never carries a
 * `search_id` today, but a structural `item` return would forward one the
 * moment the match endpoint's payload (or its type) ever gained that field —
 * exactly the AC9 leak this boundary exists to prevent. Every field below is
 * named explicitly so a future pool-only field cannot reach the search-tab
 * shared components by accident.
 */
export const poolMatchToSearchResult = (
  item: PoolMatchFreelancer,
): FreelanceSheetData => ({
  id: item.id,
  full_name: item.full_name,
  avatar_url: item.avatar_url,
  job_title: item.job_title,
  job_category: item.job_category,
  job_category_secondary: item.job_category_secondary,
  job_subcategories: item.job_subcategories,
  skills: item.skills,
  weekly_hours: item.weekly_hours,
  price_per_day: item.price_per_day,
  work_preference: item.work_preference,
  available_from: item.available_from,
  languages: item.languages,
  residency: item.residency,
  total_experience_years: item.total_experience_years,
  already_applied: item.already_applied,
  confidence_score: item.confidence_score,
  confidence_score_breakdown: item.confidence_score_breakdown,
});
